import { Button } from '@/components/ui/button';
import type { VersionedWording } from '@/lib/versioned-labels';
import { versionedDay } from '@/lib/versioned-labels';

/**
 * « Retirer ce critère », and its mirror once it is done (refonte modulaire §4;
 * maquette `Editeur.dc.html`).
 *
 * ## The retirement is always possible, and that is the decision
 *
 * §4: « le retrait reste toujours possible, même quand une version en
 * production cite l'objet ; en contrepartie, le référent porte un avertissement
 * qui dit quoi faire ("remplacer le critère X, retiré le ...") et qui ne
 * disparaît qu'une fois la référence remplacée ». So this panel never blocks -
 * it says what the retirement will cost, and the box behind the button lists
 * the referents that will carry the warning.
 *
 * It sits at the BOTTOM of the right column, under the history and the usage,
 * because it is the one gesture on this screen nobody arrives looking for. And
 * it is `destructive` in outline and never filled: the charte reserves a filled
 * noyau for the primary action, and the primary action of this screen is saving
 * a version.
 *
 * ## The four sentences come from the object
 *
 * Every one of them names it - « Retirer ce critère », « remplacer le
 * critère X » - and a layer blind to content cannot write them. They are
 * sentences in `VersionedWording` rather than a noun this file would splice in,
 * because an article and an agreement are not a concatenation: the day an
 * object is feminine, a template would read « le expérience » and nothing here
 * would catch it.
 */

export interface RetirementPanelProps {
  wording: VersionedWording;
  /** The entity's current name - what the two sentences name. */
  name: string;
  /** When it was retired, or null while it is live. */
  retiredAt: string | null;
  busy: boolean;
  onRetire: () => void;
  onRestore: () => void;
}

export function RetirementPanel({
  wording,
  name,
  retiredAt,
  busy,
  onRetire,
  onRestore,
}: RetirementPanelProps) {
  const { retirement } = wording;

  if (retiredAt !== null) {
    return (
      <section
        data-testid={`${wording.entity}-retrait`}
        className="space-y-2.5 rounded-md border-2 border-gray-200 bg-white p-5"
      >
        <h2 className="font-semibold text-gray-800 text-sm">{retirement.retiredTitle}</h2>
        <p className="text-gray-600 text-xs leading-4">
          {retirement.retiredHint(name, versionedDay(retiredAt))}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`${wording.entity}-restaurer`}
          disabled={busy}
          onClick={onRestore}
          className="self-start"
        >
          Restaurer
        </Button>
      </section>
    );
  }

  return (
    <section
      data-testid={`${wording.entity}-retrait`}
      className="space-y-2.5 rounded-md border-2 border-gray-200 bg-white p-5"
    >
      <h2 className="font-semibold text-gray-800 text-sm">{retirement.panelTitle}</h2>
      <p className="text-gray-600 text-xs leading-4">{retirement.panelHint(name)}</p>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        data-testid={`${wording.entity}-retirer`}
        disabled={busy}
        onClick={onRetire}
        className="self-start"
      >
        Retirer
      </Button>
    </section>
  );
}
